/**
 * Shared TypeBox schemas for the agent memory surface (MEM-10, COP-032, D-19,
 * D-20).
 *
 * Embedding columns are NOT exposed — 1536-dim float vectors bloat responses
 * by ~12 KB and have zero client utility.
 */
import { Type } from '@sinclair/typebox';
import { IsoDateTimeSchema, UuidSchema } from './common.js';
// Drizzle-row JSON view of tf_user_memory.
export const MemoryRowSchema = Type.Object({
    id: UuidSchema,
    userId: UuidSchema,
    trackId: Type.Union([UuidSchema, Type.Null()]),
    scope: Type.Union([Type.Literal('global'), Type.Literal('track')]),
    content: Type.String(),
    createdAt: IsoDateTimeSchema,
    deletedAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
});
// GET /memory
export const MemoryListQuerySchema = Type.Object({
    scope: Type.Optional(Type.Union([Type.Literal('global'), Type.Literal('track')])),
    trackId: Type.Optional(UuidSchema),
});
export const MemoryListResponseSchema = Type.Object({
    memories: Type.Array(MemoryRowSchema),
});
// DELETE /memory/:id
export const MemoryIdParamsSchema = Type.Object({ id: UuidSchema });
// GET /users/:id/memory — admin-only per-user list (D-20).
// Path params for the admin route; query schema is shared with MemoryListQuerySchema.
export const AdminUserMemoryParamsSchema = Type.Object({ id: UuidSchema });
// GET /sessions/:id/summary
export const SessionSummaryResponseSchema = Type.Object({
    summary: Type.Union([
        Type.Object({
            id: UuidSchema,
            sessionId: UuidSchema,
            userId: UuidSchema,
            trackId: Type.Union([UuidSchema, Type.Null()]),
            content: Type.String(),
            createdAt: IsoDateTimeSchema,
            deletedAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
        }),
        Type.Null(),
    ]),
});
// COP-051 envelope (NOT a Hub REST body — declared here so the Copilot widget
// + runtime share the same parse contract; the in-stream chat-message path
// is the only carrier; see D-13).
export const MemoryEnvelopeSchema = Type.Union([
    Type.Object({
        kind: Type.Literal('memory_confirm'),
        suggestionId: UuidSchema,
        content: Type.String(),
        scope: Type.Union([Type.Literal('global'), Type.Literal('track')]),
        trackId: Type.Optional(UuidSchema),
        // IMEM-14 — present when the confirm targets an EXISTING memory (a refine
        // proposal). The client echoes it; the server binds the write to the
        // suggestion ROW (semantics decided in task-03).
        updatesMemoryId: Type.Optional(Type.String({ format: 'uuid' })),
    }),
    Type.Object({
        kind: Type.Literal('memory_dismiss'),
        suggestionId: UuidSchema,
    }),
]);
// IMEM-19 / COP-105 in-chat forget envelope (NOT a Hub REST body — shares the
// chat-message carrier with MemoryEnvelopeSchema so the Copilot widget + runtime
// share one parse contract). Confirm/dismiss carry ONLY `suggestionId`: the
// server holds the authoritative binding (the exact memory ids/content/scope)
// in its in-process pending-forget store, so nothing consequential is echoed
// from the client — a delete must never trust the client echo (the doc-share
// pattern). `additionalProperties: false` keeps the delete envelope tight.
export const ForgetMemoryEnvelopeSchema = Type.Union([
    Type.Object({
        kind: Type.Literal('forget_confirm'),
        suggestionId: Type.String({ format: 'uuid' }),
    }, { additionalProperties: false }),
    Type.Object({
        kind: Type.Literal('forget_dismiss'),
        suggestionId: Type.String({ format: 'uuid' }),
    }, { additionalProperties: false }),
]);
//# sourceMappingURL=memory.js.map